import React, { useState } from 'react';
import styled from 'styled-components';

import { media } from '@/styles/theme';
import type { ContactFormProps } from '@/types';

interface ContactFormScreenProps extends ContactFormProps {
  rows?: number;
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 0;
  min-width: 250px;

  ${media.tablet} {
    max-width: 100%;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius};
  background: #fbfbfb;
  border: 1px solid #eaeaea;
  margin-bottom: 8px;
  resize: none;
  padding: 6px;

  &::placeholder {
    color: #b9b9b9;
  }
`;

const Button = styled.button<{ disabled: boolean }>`
  color: white;
  font-size: 12px;
  width: 85px;
  height: 24px;
  line-height: 22px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius};
  cursor: pointer;
  transition-duration: ${({ theme }) => theme.transitionDuration};

  &:hover {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.25);
  }

  ${({ disabled }) =>
    disabled &&
    `
    background-color: gray !important;
    cursor: not-allowed;
    
    &:hover {
      box-shadow: none;
    }
  `}
`;

const SuccessMessage = styled.p`
  font-size: 16px;
  color: #555555;
  margin: 14px 0;

  ${media.mobile} {
    text-align: center;
  }
`;

const ErrorMessage = styled.p`
  font-size: 12px;
  color: #bc7c84;
  margin: 8px 0 0 0;

  ${media.mobile} {
    margin-bottom: 14px;
    text-align: center;
  }
`;

const ContactFormScreen: React.FC<ContactFormScreenProps> = ({
  primaryColor,
  sendMessage,
  rows = 4,
}) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendResult, setSendResult] = useState<'success' | 'error' | null>(
    null
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await sendMessage(message);
      console.log('response', response);
      // Assuming response is successful if no error is thrown
      setSendResult('success');
      setLoading(false);
    } catch (error) {
      console.error('Error sending message:', error);
      setSendResult('error');
      setLoading(false);
    }
  };

  const disabled = !message || loading;

  if (sendResult === 'success') {
    return (
      <SuccessMessage>
        Message sent successfully. Thank you for your feedback!
      </SuccessMessage>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <TextArea
        value={message}
        onChange={handleChange}
        rows={rows}
        placeholder="Enter your feedback"
      />
      <Button style={{ backgroundColor: primaryColor }} disabled={disabled}>
        {loading ? 'Sending...' : 'Send'}
      </Button>
      {sendResult === 'error' && (
        <ErrorMessage>
          There was a problem sending your message. Please try again.
        </ErrorMessage>
      )}
    </Form>
  );
};

export default ContactFormScreen;
